import React from 'react';
import { Box, Paper, Typography, Chip, Avatar, Button, Divider, Stack, useTheme, alpha } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStore, faCalendarAlt, faArrowRight, faCreditCard, faHashtag } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next'; // <--- Importante

export default function OrderCard({ order, onViewDetails, onPayRetry }) {
    const theme = useTheme();
    const { t } = useTranslation(); // <--- Hook
    
    // Destructuring
    const { status, merchant, can_pay, amount, currency, code, formatted_date } = order;

    return (
        <Paper 
            elevation={0}
            sx={{ 
                mb: 2, 
                border: '1px solid', borderColor: 'divider', borderRadius: 3,
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                '&:hover': { 
                    borderColor: 'primary.main', 
                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
                    transform: 'translateY(-2px)'
                }
            }}
        >
            {/* --- PARTE SUPERIOR --- */}
            <Box p={2} display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
                
                {/* IZQUIERDA */}
                <Box display="flex" gap={2} alignItems="center">
                    <Avatar 
                        src={merchant.logo} 
                        alt={merchant.name}
                        variant="rounded"
                        sx={{ width: 56, height: 56, bgcolor: 'grey.50', border: '1px solid #eee' }}
                    >
                        <FontAwesomeIcon icon={faStore} color="#ccc"/>
                    </Avatar>
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold" lineHeight={1.2} mb={0.5}>
                            {merchant.name}
                        </Typography>
                        
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <Box display="flex" alignItems="center" gap={0.5} color="text.secondary">
                                <FontAwesomeIcon icon={faCalendarAlt} size="xs"/>
                                <Typography variant="caption" fontWeight="500">{formatted_date}</Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={0.5} color="text.secondary">
                                <FontAwesomeIcon icon={faHashtag} size="xs"/>
                                <Typography variant="caption" fontWeight="500">{code}</Typography>
                            </Box>
                        </Stack>
                    </Box>
                </Box>

                {/* DERECHA */}
                <Box textAlign="right">
                    <Typography variant="caption" display="block" color="text.secondary" mb={0.5}>
                        {t('orders.card.total')}
                    </Typography>
                    <Typography variant="h6" fontWeight="800" color="text.primary" sx={{ lineHeight: 1 }}>
                        ${amount.toFixed(2)} <Typography component="span" variant="caption" color="text.secondary">{currency}</Typography>
                    </Typography>
                    
                    <Box mt={1} display="flex" justifyContent="flex-end">
                        <Chip 
                            label={status.label} // Nota: Esto viene del backend. Si quieres traducirlo, el backend debería mandar una 'key' en vez del texto en inglés.
                            color={status.color} 
                            size="small" 
                            sx={{ fontWeight: 'bold', borderRadius: 1, height: 24 }}
                        />
                    </Box>
                </Box>
            </Box>

            <Divider />

            {/* --- PARTE INFERIOR: ACCIONES --- */}
            <Box p={1.5} bgcolor="grey.50" display="flex" justifyContent="flex-end" gap={1}>
                <Button 
                    variant="text" 
                    color="inherit" 
                    size="small" 
                    endIcon={<FontAwesomeIcon icon={faArrowRight} size="xs"/>}
                    onClick={() => onViewDetails(order.id)}
                    sx={{ borderRadius: 2, px: 2, color: 'text.secondary' }}
                >
                    {t('orders.card.view_details')}
                </Button>

                {can_pay && (
                    <Button 
                        variant="contained" 
                        color="primary" 
                        size="small" 
                        startIcon={<FontAwesomeIcon icon={faCreditCard} size="xs"/>}
                        onClick={() => onPayRetry(order)}
                        sx={{ borderRadius: 2, px: 3, fontWeight: 'bold', boxShadow: 'none' }}
                    >
                        {t('orders.card.pay_now')}
                    </Button>
                )}
            </Box>
        </Paper>
    );
}